import React, { useEffect, useState } from 'react';
import { Dialog, ModalMask } from '@fx-ui/fine-design';
import classNames from 'classnames';
import Captcha from './captcha';
import { assign, ECode, get } from '@libs/shared';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import Base64 from 'crypto-js/enc-base64';
import TurnstileDialog from '../turnstile-dialog';
import RecaptchaDialog from '../recaptcha-dialog';
import { globalState } from '../../models';
import { getErrorMsg, post } from '../../utils';
import { platformIntl } from '../../constants';

interface ICaptchaDialogProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    data: any;
    url: string;
}

const KEY = 'Zd3apeP0';

const GraphicCaptchaDialog: React.FC<ICaptchaDialogProps> = (props) => {
    const { visible, onClose, onSuccess, data, url } = props;
    const [val, setVal] = useState<string>('');
    const [errInfo, setErrInfo] = useState({
        err: false,
        msg: ''
    });
    const [refreshToken, setRefreshToken] = useState<number>();

    useEffect(() => {
        if (visible) {
            setVal('');
            setErrInfo({
                err: false,
                msg: ''
            });
            setRefreshToken(undefined);
        }
    }, [visible]);

    const verifyCaptcha = () => {
        post(
            url,
            assign({}, data, {
                captcha: Base64.stringify(HmacSHA256(val.toLowerCase(), KEY))
            }),
            {
                customHandleErrorCodes: [
                    ECode.EMAIL_FORMAT_IS_INVALID,
                    ECode.USER_PHONE_FORMAT_INVALID,
                    ECode.USER_PHONE_REQUIRED,
                    ECode.USER_WRONG_AUTH_CODE_CAPTCHA,
                    ECode.USER_VERIFY_CODE_SEND_LIMIT_EXCEEDED,
                    ECode.USER_PHONE_INVALID,
                    ECode.USER_EMAIL_IS_INCORRECT,
                    ECode.USER_NO_AUTH_CODE_CAPTCHA
                ]
            }
        )
            .then(() => {
                onSuccess();
            })
            .catch((res) => {
                setRefreshToken(new Date().getTime());
                const err = get(res, 'response.data');
                setErrInfo({
                    err: true,
                    msg: getErrorMsg(err)
                });
            });
    };

    if (globalState.isMobile) {
        return (
            <ModalMask visible={visible} maskStyle={{ background: 'rgba(0, 0, 0, .25)' }}>
                <div className="fx-mobile-captcha-dialog">
                    <div
                        className={classNames('dialog-content', {
                            error: errInfo.err && errInfo.msg
                        })}
                    >
                        <div className="captcha-title">
                            {platformIntl.get('account.register.captcha.input')}
                        </div>
                        <Captcha
                            className="captcha-wrapper"
                            value={val}
                            onChange={(v: string) => setVal(v)}
                            error={errInfo.err}
                            refreshToken={refreshToken}
                        />
                        {errInfo.err && errInfo.msg && (
                            <div className="captcha-error-msg">{errInfo.msg}</div>
                        )}
                    </div>
                    <div className="dialog-footer">
                        <div className="dialog-btn cancel" onClick={onClose}>
                            {platformIntl.get('cancel')}
                        </div>
                        <div className="dialog-btn ok" onClick={verifyCaptcha}>
                            {platformIntl.get('ok')}
                        </div>
                    </div>
                </div>
            </ModalMask>
        );
    } else {
        return (
            <Dialog
                className="fx-captcha-dialog"
                visible={visible}
                closable={false}
                separator={false}
                width={360}
                height={errInfo.err && errInfo.msg ? 192 : 166}
                onOk={verifyCaptcha}
                onCancel={onClose}
            >
                <div className="captcha-title">
                    {platformIntl.get('account.register.captcha.input')}
                </div>
                <Captcha
                    value={val}
                    onChange={(v: string) => setVal(v)}
                    error={errInfo.err}
                    refreshToken={refreshToken}
                />
                {errInfo.err && errInfo.msg && (
                    <div className="captcha-error-msg">{errInfo.msg}</div>
                )}
            </Dialog>
        );
    }
};

const CaptchaDialog: React.FC<ICaptchaDialogProps> = (props) => {
    const type = globalState.options?.captcha?.type;
    if (props.visible && type === 'cloud_flare') {
        return <TurnstileDialog {...props} />;
    }
    if (props.visible && type === 'google_recaptcha') {
        return <RecaptchaDialog {...props} />;
    }
    return <GraphicCaptchaDialog {...props} />;
};

export default CaptchaDialog;
